import { motion } from 'motion/react'
import styled, { css } from 'styled-components'

import { mediaMaxTablet } from '@blade/ui-theme'
import { SettingsSessionManagement } from '@root/features/auth/session-management/ui'
import { SetupOtp } from '@root/features/auth/setup-otp/ui'
import { UpdatePassword } from '@root/features/auth/update-password/ui'
import { type SettingScreen } from '@root/features/settings/settings-screen/atoms'

import { SettingsAboutTheApp } from './settings-about-the-app'
import { SettingsAccountAndPrivacy } from './settings-account'
import { SettingsAppSettings } from './settings-app-settings'
import { SettingsAppSettingsCustomTheme } from './settings-app-settings-custom-theme'
import { SettingsAppSettingsTheme } from './settings-app-settings-theme'
import { SettingsLanguages } from './settings-languages'
import { SettingsMain } from './settings-main'
import { SettingsPolicies } from './settings-policies'
import { SettingsSecurity } from './settings-security'
import { SettingsShortcuts } from './settings-shortcuts'
import { SettingsTradingMode } from './settings-trading-mode'

import type { SetupOtpMode } from '@root/features/auth/setup-otp/ui'
import type { ClassNameProps } from '@root/shared/react'

type SettingsViewProps = {
  screen: SettingScreen | undefined
  optMode: SetupOtpMode
  setOptMode: (mode: SetupOtpMode) => void
  setScreen: (screen: SettingScreen) => void
} & ClassNameProps

export const SettingsView: React.FC<SettingsViewProps> = ({
  screen: view,
  optMode,
  setOptMode,
  setScreen,
  className,
}) => {
  if (!view) {
    return undefined
  }

  const settingsViews: Record<SettingScreen, React.ReactNode> = {
    main: <SettingsMain />,
    security: <SettingsSecurity />,
    shortcuts: <SettingsShortcuts />,
    sessions: <SettingsSessionManagement />,
    language: <SettingsLanguages />,
    policies: <SettingsPolicies />,
    'account-and-privacy': <SettingsAccountAndPrivacy />,
    'trading-mode': <SettingsTradingMode />,
    'terms-of-service': <SettingsAboutTheApp policy="terms-of-service" />,
    'privacy-policy': <SettingsAboutTheApp policy="privacy-policy" />,
    'anti-money-laundering-policy': (
      <SettingsAboutTheApp policy="anti-money-laundering-policy" />
    ),
    'boosting-terms': <SettingsAboutTheApp policy="boosting-terms" />,
    'disclaimer-terms': <SettingsAboutTheApp policy="disclaimer-terms" />,
    'affiliate-program-terms': (
      <SettingsAboutTheApp policy="affiliate-program-terms" />
    ),
    'live-streaming-terms': (
      <SettingsAboutTheApp policy="live-streaming-terms" />
    ),
    'token-creation-and-promotion-terms': (
      <SettingsAboutTheApp policy="token-creation-and-promotion-terms" />
    ),
    'legal-entity-access-terms': (
      <SettingsAboutTheApp policy="legal-entity-access-terms" />
    ),
    'setup-otp': (
      <SetupOtp
        onBack={() => setScreen('security')}
        mode={optMode}
        setMode={setOptMode}
      />
    ),
    'update-password': (
      <UpdatePassword
        type="password"
        onPasswordUpdated={() => setScreen('security')}
      />
    ),
    'update-local-password': (
      <UpdatePassword
        type="local-password"
        onPasswordUpdated={() => setScreen('security')}
      />
    ),
    'app-settings': <SettingsAppSettings />,
    'app-settings-theme': <SettingsAppSettingsTheme />,
    'app-settings-custom-theme': <SettingsAppSettingsCustomTheme />,
  }

  return (
    <Root
      className={className}
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.2 }}
      key={view}
      data-screen={view}
    >
      {settingsViews[view]}
    </Root>
  )
}

const Root = styled(motion.div)`
  min-height: 380px;
  display: grid;

  ${mediaMaxTablet(css`
    height: 100%;
    min-height: 0;
    overflow: auto;

    &[data-screen='account-and-privacy'],
    &[data-screen='terms-of-service'],
    &[data-screen='privacy-policy'],
    &[data-screen='anti-money-laundering-policy'],
    &[data-screen='boosting-terms'],
    &[data-screen='disclaimer-terms'],
    &[data-screen='affiliate-program-terms'],
    &[data-screen='live-streaming-terms'],
    &[data-screen='token-creation-and-promotion-terms'],
    &[data-screen='legal-entity-access-terms'] {
      margin-top: 10px;
    }
  `)}
`
